<template>
    <div class="profile-page">
        <!-- 背景装饰 -->
        <div class="profile-background">
            <div class="floating-particles">
                <div v-for="n in 12" :key="n" class="particle" :class="`particle-${n}`"></div>
            </div>
            <div class="grid-overlay"></div>
        </div>

        <!-- 主要内容 -->
        <div class="profile-container">


            <!-- 用户卡片 -->
            <div class="user-card">
                <div class="card-background"></div>
                <div class="user-avatar-section">
                    <div class="avatar-container">
                        <img :src="userProfile.avatar_url || '/images/default-avatar.png'"
                            :alt="userProfile.display_name" class="user-avatar" />
                        <div class="avatar-overlay">
                            <button @click="showAvatarUpload = true" class="change-avatar-btn">
                                <CameraIcon :size="20" />
                            </button>
                        </div>
                    </div>
                    <div class="user-info">
                        <h2 class="user-name">{{ userProfile.display_name || userProfile.username }}</h2>
                        <p class="user-email">{{ userProfile.email }}</p>
                        <div class="user-status">
                            <span class="status-dot"></span>
                            <span>在线</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 主要功能区域 -->
            <div class="profile-content">
                <!-- 左侧导航 -->
                <div class="profile-nav">
                    <div class="nav-items">
                        <button v-for="tab in tabs" :key="tab.id" @click="handleTabClick(tab.id)"
                            :class="['nav-item', { active: activeTab === tab.id && tab.id !== 'logout', 'logout-tab': tab.id === 'logout' }]">
                            <component :is="tab.icon" :size="20" />
                            <span>{{ tab.label }}</span>
                        </button>
                    </div>
                </div>

                <!-- 右侧内容区域 -->
                <div class="profile-main">
                    <!-- 基本信息编辑 -->
                    <div v-if="activeTab === 'basic'" class="content-section">
                        <div class="section-header">
                            <h3>基本信息</h3>
                            <p>管理您的基本个人信息</p>
                        </div>

                        <form @submit.prevent="updateProfile" class="profile-form">
                            <div class="form-row">
                                <div class="input-group">
                                    <label class="input-label">用户名</label>
                                    <input v-model="editForm.username" type="text" class="form-input"
                                        :disabled="!canUpdate" placeholder="请输入用户名" maxlength="30" />
                                </div>
                                <div class="input-group">
                                    <label class="input-label">昵称</label>
                                    <input v-model="editForm.display_name" type="text" class="form-input"
                                        :disabled="!canUpdate" placeholder="请输入昵称" maxlength="50" />
                                </div>
                            </div>

                            <div class="update-notice" v-if="!canUpdate">
                                <InfoIcon :size="16" />
                                <span>距离下次可更新还有 {{ remainingDays }} 天</span>
                            </div>

                            <div class="form-actions">
                                <button type="submit" class="submit-btn" :disabled="!canUpdate || updating">
                                    <span v-if="updating">更新中...</span>
                                    <span v-else>保存更改</span>
                                </button>
                            </div>
                        </form>
                    </div>

                    <!-- 密码重置 -->
                    <div v-if="activeTab === 'password'" class="content-section">
                        <div class="section-header">
                            <h3>密码设置</h3>
                            <p>更改您的登录密码</p>
                        </div>

                        <form @submit.prevent="updatePassword" class="profile-form">
                            <div class="input-group">
                                <label class="input-label">当前密码</label>
                                <div class="input-with-icon">
                                    <input v-model="passwordForm.currentPassword"
                                        :type="showCurrentPassword ? 'text' : 'password'" class="form-input"
                                        placeholder="请输入当前密码" required />
                                    <button type="button" class="input-icon-btn"
                                        @click="showCurrentPassword = !showCurrentPassword">
                                        <EyeIcon v-if="!showCurrentPassword" :size="18" />
                                        <EyeOffIcon v-else :size="18" />
                                    </button>
                                </div>
                            </div>

                            <div class="input-group">
                                <label class="input-label">新密码</label>
                                <div class="input-with-icon">
                                    <input v-model="passwordForm.newPassword"
                                        :type="showNewPassword ? 'text' : 'password'" class="form-input"
                                        placeholder="请输入新密码（至少6位）" @input="checkPasswordStrength" required />
                                    <button type="button" class="input-icon-btn"
                                        @click="showNewPassword = !showNewPassword">
                                        <EyeIcon v-if="!showNewPassword" :size="18" />
                                        <EyeOffIcon v-else :size="18" />
                                    </button>
                                </div>
                                <!-- 密码强度指示器 -->
                                <div v-if="passwordForm.newPassword" class="password-strength">
                                    <div class="strength-bar">
                                        <div class="strength-fill"
                                            :style="{ width: passwordStrength.width, background: passwordStrength.color }">
                                        </div>
                                    </div>
                                    <span class="strength-text" :style="{ color: passwordStrength.color }">{{
                                        passwordStrength.text }}</span>
                                </div>
                            </div>

                            <div class="input-group">
                                <label class="input-label">确认新密码</label>
                                <div class="input-with-icon">
                                    <input v-model="passwordForm.confirmPassword"
                                        :type="showConfirmPassword ? 'text' : 'password'" class="form-input"
                                        :class="{ 'input-error': passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword }"
                                        placeholder="请再次输入新密码" required />
                                    <button type="button" class="input-icon-btn"
                                        @click="showConfirmPassword = !showConfirmPassword">
                                        <EyeIcon v-if="!showConfirmPassword" :size="18" />
                                        <EyeOffIcon v-else :size="18" />
                                    </button>
                                </div>
                                <div v-if="passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword"
                                    class="error-message">
                                    两次输入的密码不一致
                                </div>
                            </div>

                            <div class="form-actions">
                                <button type="submit" class="submit-btn"
                                    :disabled="!canUpdatePassword || updatingPassword">
                                    <span v-if="updatingPassword">更新中...</span>
                                    <span v-else>更新密码</span>
                                </button>
                            </div>
                        </form>
                    </div>

                    <!-- 我的评论 -->
                    <div v-if="activeTab === 'comments'" class="content-section">
                        <div class="section-header">
                            <h3>我的评论</h3>
                            <p>查看和管理您发布的所有评论</p>
                        </div>

                        <div class="comments-container">
                            <div v-if="loading" class="loading-state">
                                <div class="loading-spinner"></div>
                                <span>加载中...</span>
                            </div>

                            <div v-else-if="userComments.length === 0" class="empty-state">
                                <MessageSquareIcon :size="48" />
                                <h4>暂无评论</h4>
                                <p>您还没有发布任何评论</p>
                            </div>

                            <div v-else class="comments-list">
                                <div v-for="comment in userComments" :key="comment.id" class="comment-item">
                                    <div class="comment-header">
                                        <h4 class="article-title">{{ comment.articles?.title || '文章已删除' }}</h4>
                                        <time class="comment-date">{{ formatDate(comment.created_at) }}</time>
                                    </div>
                                    <div class="comment-content">
                                        <p>{{ comment.content }}</p>
                                    </div>
                                    <div class="comment-actions">
                                        <router-link v-if="comment.articles" :to="`/article/${comment.articles.id}`"
                                            class="view-article-btn">
                                            查看文章
                                        </router-link>
                                        <button @click="deleteComment(comment.id)" class="delete-comment-btn">
                                            删除评论
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 头像上传模态框 -->
        <div v-if="showAvatarUpload" class="modal-overlay" @click="showAvatarUpload = false">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h3>更换头像</h3>
                    <button @click="showAvatarUpload = false" class="close-btn">
                        <XIcon :size="20" />
                    </button>
                </div>
                <div class="modal-body">
                    <div class="avatar-upload">
                        <input ref="avatarInput" type="file" accept="image/*" @change="handleAvatarUpload"
                            class="hidden-input" />
                        <button @click="$refs.avatarInput.click()" class="upload-btn">
                            <UploadIcon :size="24" />
                            <span>选择图片</span>
                        </button>
                        <p class="upload-tip">支持 JPG、PNG 格式，文件大小不超过 2MB</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
// import { message } from 'ant-design-vue' // 如果没有安装 ant-design-vue，我们创建简单的消息提示
const message = {
    success: (msg) => alert('成功: ' + msg),
    error: (msg) => alert('错误: ' + msg),
    warning: (msg) => alert('警告: ' + msg)
}
import {
    UserIcon,
    LockIcon,
    MessageSquareIcon,
    CameraIcon,
    EyeIcon,
    EyeOffIcon,
    InfoIcon,
    XIcon,
    UploadIcon,
    LogOutIcon
} from 'lucide-vue-next'
import supabaseAuthService from '../services/supabaseAuthService.js'
import supabaseProfileService from '../services/supabaseProfileService.js'

export default {
    name: 'Profile',
    components: {
        UserIcon,
        LockIcon,
        MessageSquareIcon,
        CameraIcon,
        EyeIcon,
        EyeOffIcon,
        InfoIcon,
        XIcon,
        UploadIcon,
        LogOutIcon
    },
    data() {
        return {
            activeTab: 'basic',
            userProfile: {},
            loading: false,
            updating: false,
            updatingPassword: false,
            showAvatarUpload: false,

            // 表单数据
            editForm: {
                username: '',
                display_name: ''
            },
            passwordForm: {
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            },

            // 密码显示状态
            showCurrentPassword: false,
            showNewPassword: false,
            showConfirmPassword: false,

            // 密码强度
            passwordStrength: {
                width: '0%',
                color: '#ef4444',
                text: ''
            },

            // 评论数据
            userComments: [],

            // 更新限制
            lastUpdateTime: null,

            tabs: [
                { id: 'basic', label: '基本信息', icon: 'UserIcon' },
                { id: 'password', label: '密码设置', icon: 'LockIcon' },
                { id: 'comments', label: '我的评论', icon: 'MessageSquareIcon' },
                { id: 'logout', label: '退出登录', icon: 'LogOutIcon' }
            ]
        }
    },
    computed: {
        canUpdate() {
            if (!this.lastUpdateTime) return true
            const now = new Date()
            const lastUpdate = new Date(this.lastUpdateTime)
            const diffDays = Math.floor((now - lastUpdate) / (1000 * 60 * 60 * 24))
            return diffDays >= 3
        },
        remainingDays() {
            if (!this.lastUpdateTime) return 0
            const now = new Date()
            const lastUpdate = new Date(this.lastUpdateTime)
            const diffDays = Math.floor((now - lastUpdate) / (1000 * 60 * 60 * 24))
            return Math.max(0, 3 - diffDays)
        },
        canUpdatePassword() {
            return this.passwordForm.currentPassword &&
                this.passwordForm.newPassword &&
                this.passwordForm.newPassword === this.passwordForm.confirmPassword &&
                this.passwordForm.newPassword.length >= 6
        }
    },
    async mounted() {
        await this.loadUserProfile()
        if (this.activeTab === 'comments') {
            await this.loadUserComments()
        }
    },
    watch: {
        activeTab(newTab) {
            if (newTab === 'comments' && this.userComments.length === 0) {
                this.loadUserComments()
            }
        }
    },
    methods: {
        async loadUserProfile() {
            try {
                this.loading = true
                const user = await supabaseProfileService.getCurrentUser()
                if (user) {
                    const profile = await supabaseProfileService.getUserProfile(user.id)
                    this.userProfile = profile
                    this.editForm.username = profile.username || ''
                    this.editForm.display_name = profile.display_name || ''
                    this.lastUpdateTime = profile.updated_at
                }
            } catch (error) {
                message.error('加载用户信息失败')
            } finally {
                this.loading = false
            }
        },

        async updateProfile() {
            if (!this.canUpdate) {
                message.warning('距离上次更新不足3天，暂时无法修改')
                return
            }

            try {
                this.updating = true
                const user = await supabaseProfileService.getCurrentUser()
                if (user) {
                    await supabaseProfileService.updateProfile(user.id, {
                        username: this.editForm.username,
                        display_name: this.editForm.display_name
                    })
                    message.success('个人信息更新成功')
                    await this.loadUserProfile()
                }
            } catch (error) {
                message.error(error.message || '更新失败')
            } finally {
                this.updating = false
            }
        },

        async updatePassword() {
            try {
                this.updatingPassword = true
                await supabaseAuthService.updatePassword(
                    this.passwordForm.newPassword
                )

                message.success('密码更新成功')
                this.passwordForm = {
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                }
            } catch (error) {
                message.error(error.message || '密码更新失败')
            } finally {
                this.updatingPassword = false
            }
        },

        async loadUserComments() {
            try {
                this.loading = true
                const user = await supabaseProfileService.getCurrentUser()
                if (user) {
                    this.userComments = await supabaseProfileService.getUserComments(user.id)
                    console.log('用户评论数据:', this.userComments) // 调试日志
                }
            } catch (error) {
                message.error('加载评论失败')
            } finally {
                this.loading = false
            }
        },

        checkPasswordStrength() {
            const password = this.passwordForm.newPassword
            if (!password) {
                this.passwordStrength = { width: '0%', color: '#ef4444', text: '' }
                return
            }

            let score = 0
            let tips = []

            if (password.length >= 8) score += 1
            else tips.push('至少8位字符')

            if (/\d/.test(password)) score += 1
            else tips.push('包含数字')

            if (/[a-z]/.test(password)) score += 1
            else tips.push('包含小写字母')

            if (/[A-Z]/.test(password)) score += 1
            else tips.push('包含大写字母')

            if (/[^a-zA-Z0-9]/.test(password)) score += 1
            else tips.push('包含特殊字符')

            if (score <= 2) {
                this.passwordStrength = {
                    width: '33%',
                    color: '#ef4444',
                    text: `弱 - 建议${tips.slice(0, 2).join('、')}`
                }
            } else if (score <= 3) {
                this.passwordStrength = {
                    width: '66%',
                    color: '#f59e0b',
                    text: `中等 - 建议${tips.slice(0, 1).join('、')}`
                }
            } else {
                this.passwordStrength = {
                    width: '100%',
                    color: '#00c851',
                    text: '强 - 密码安全性良好'
                }
            }
        },

        async handleAvatarUpload(event) {
            const file = event.target.files[0]
            if (!file) return

            if (file.size > 2 * 1024 * 1024) {
                message.error('图片大小不能超过2MB')
                return
            }

            try {
                const user = await supabaseProfileService.getCurrentUser()
                if (user) {
                    const updatedProfile = await supabaseProfileService.uploadAvatar(user.id, file)
                    this.userProfile.avatar_url = updatedProfile.avatar_url
                    this.showAvatarUpload = false
                    message.success('头像更新成功')
                }
            } catch (error) {
                message.error('头像上传失败')
            }
        },

        formatDate(dateString) {
            return new Date(dateString).toLocaleDateString('zh-CN', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        },

        async deleteComment(commentId) {
            if (!confirm('确定要删除这条评论吗？')) {
                return
            }

            try {
                const user = await supabaseProfileService.getCurrentUser()
                if (user) {
                    await supabaseProfileService.deleteComment(commentId, user.id)
                    message.success('评论删除成功')
                    // 重新加载评论列表
                    await this.loadUserComments()
                }
            } catch (error) {
                message.error('删除评论失败')
            }
        },

        handleTabClick(tabId) {
            if (tabId === 'logout') {
                this.logout()
            } else {
                this.activeTab = tabId
            }
        },

        async logout() {
            if (!confirm('确定要退出登录吗？')) {
                return
            }

            try {
                await supabaseAuthService.signOut()
                message.success('退出登录成功')
                // 跳转到登录页面或首页
                this.$router.push('/auth')
            } catch (error) {
                message.error('退出登录失败')
            }
        }
    }
}
</script>

<style scoped>
/* 主容器 */
.profile-page {
    height: calc(100vh - 80px);
    background: transparent;
    position: fixed;
    top: 80px;
    left: 0;
    right: 0;
    overflow: hidden;
    padding-top: 0;
}

/* 背景装饰 - 简化版 */
.profile-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 0;
    opacity: 0.3;
}

.floating-particles {
    display: none;
    /* 隐藏浮动粒子，保持简洁 */
}

.grid-overlay {
    display: none;
    /* 隐藏网格背景 */
}

/* 移除粒子动画相关样式 */

/* 主容器 */
.profile-container {
    position: relative;
    z-index: 1;
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    height: 100%;
    overflow-y: auto;
}

/* 页面标题 - 简化样式 */
.profile-header {
    text-align: left;
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 1px solid #e5e7eb;
}

/* 移除fadeInUp动画 */

.page-title {
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 8px;
}

.page-subtitle {
    font-size: 1rem;
    color: #6b7280;
    margin: 0;
}

.header-decoration {
    display: none;
    /* 移除装饰线 */
}

/* 移除shimmer动画 */

/* 用户卡片 - 采用文章卡片样式 */
.user-card {
    background: white;
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
    border: 1px solid #e5e7eb;
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
}

/* 移除slideInLeft动画 */

.card-background {
    display: none;
    /* 移除背景渐变 */
}

.user-avatar-section {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 30px;
}

.avatar-container {
    position: relative;
}

.user-avatar {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    border: 4px solid white;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    object-fit: cover;
    transition: all 0.3s ease;
}

.avatar-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: all 0.3s ease;
    cursor: pointer;
}

.avatar-container:hover .avatar-overlay {
    opacity: 1;
}

.change-avatar-btn {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 8px;
    border-radius: 50%;
    transition: all 0.3s ease;
}

.change-avatar-btn:hover {
    background: rgba(255, 255, 255, 0.2);
}

.user-info {
    flex: 1;
}

.user-name {
    font-size: 2rem;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 8px;
}

.user-email {
    font-size: 1.1rem;
    color: #64748b;
    margin-bottom: 12px;
}

.user-status {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #00c851;
    font-weight: 500;
}

.status-dot {
    width: 8px;
    height: 8px;
    background: #00c851;
    border-radius: 50%;
    animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.5;
    }
}

/* 主要内容区域 - 采用文章页面的网格布局 */
.profile-content {
    display: grid;
    grid-template-columns: 280px 1fr;
    gap: 24px;
}

/* 移除fadeIn动画 */

/* 左侧导航 - 采用侧边栏样式 */
.profile-nav {
    background: white;
    border-radius: 16px;
    padding: 20px 0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
    border: 1px solid #e5e7eb;
    height: fit-content;
    position: sticky;
    top: 20px;
}

.nav-items {
    display: flex;
    flex-direction: column;
}

.nav-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 20px;
    background: none;
    border: none;
    color: #6b7280;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 8px;
    margin: 0 12px 4px;
    text-align: left;
    width: calc(100% - 24px);
}

.nav-item:hover {
    background: #f3f4f6;
    color: #374151;
}

.nav-item.active {
    background: #dbeafe;
    color: #1d4ed8;
}

.nav-item.logout-tab {
    color: #ef4444;
}

.nav-item.logout-tab:hover {
    background: #fef2f2;
    color: #dc2626;
}

/* 右侧内容 - 采用文章卡片样式 */
.profile-main {
    background: white;
    border-radius: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
    border: 1px solid #e5e7eb;
    overflow: hidden;
}

.content-section {
    padding: 24px;
}

.section-header {
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e5e7eb;
}

.section-header h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 4px;
}

.section-header p {
    color: #6b7280;
    font-size: 0.875rem;
    margin: 0;
}

/* 表单样式 */
.profile-form {
    max-width: 600px;
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;
}

.input-group {
    margin-bottom: 24px;
}

.input-label {
    display: block;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
    font-size: 0.95rem;
}

.form-input {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    background: white;
    transition: all 0.2s ease;
    box-sizing: border-box;
}

.form-input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input:disabled {
    background: #f8fafc;
    color: #9ca3af;
    cursor: not-allowed;
}

.form-input.input-error {
    border-color: #ef4444;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.input-with-icon {
    position: relative;
}

.input-icon-btn {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #6b7280;
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    transition: all 0.2s ease;
}

.input-icon-btn:hover {
    color: #00c851;
    background: rgba(0, 200, 81, 0.1);
}

/* 更新提醒 */
.update-notice {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fef3c7;
    color: #d97706;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 20px;
    font-weight: 500;
    font-size: 0.875rem;
    border: 1px solid #fbbf24;
}

/* 密码强度指示器 */
.password-strength {
    margin-top: 12px;
}

.strength-bar {
    width: 100%;
    height: 6px;
    background: #f1f5f9;
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 8px;
}

.strength-fill {
    height: 100%;
    transition: all 0.4s ease;
    border-radius: 3px;
}

.strength-text {
    font-size: 0.875rem;
    font-weight: 500;
}

/* 错误消息 */
.error-message {
    color: #dc2626;
    font-size: 0.875rem;
    margin-top: 8px;
    font-weight: 500;
}

/* 按钮 */
.form-actions {
    margin-top: 32px;
}

.submit-btn {
    background: #3b82f6;
    color: white;
    border: none;
    border-radius: 8px;
    padding: 12px 24px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.submit-btn:hover {
    background: #2563eb;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.submit-btn:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
}

/* 评论区域 */
.comments-container {
    min-height: 300px;
}

.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 60px 20px;
    color: #64748b;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #f3f4f6;
    border-top: 3px solid #00c851;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 60px 20px;
    color: #64748b;
    text-align: center;
}

.empty-state h4 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #374151;
    margin: 0;
}

.comments-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px;
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fafbfc;
}

.comment-item {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 12px;
    transition: all 0.2s ease;
}

.comment-item:hover {
    border-color: #d1d5db;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.comment-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.article-title {
    font-size: 1rem;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
}

.comment-date {
    color: #64748b;
    font-size: 0.8rem;
}

.comment-content {
    margin-bottom: 10px;
}

.comment-content p {
    color: #374151;
    line-height: 1.6;
    margin: 0;
}

.comment-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}

.view-article-btn {
    color: #3b82f6;
    text-decoration: none;
    font-weight: 500;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.8rem;
    transition: all 0.2s ease;
}

.view-article-btn:hover {
    background: #dbeafe;
    text-decoration: none;
}

/* 删除评论按钮 */
.delete-comment-btn {
    color: #ef4444;
    background: none;
    border: none;
    font-weight: 500;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.2s ease;
}

.delete-comment-btn:hover {
    background: #fef2f2;
    color: #dc2626;
}

/* 模态框 */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    border-radius: 12px;
    max-width: 500px;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 24px 0;
    margin-bottom: 20px;
}

.modal-header h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
}

.close-btn {
    background: none;
    border: none;
    color: #64748b;
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    transition: all 0.2s ease;
}

.close-btn:hover {
    background: #f1f5f9;
    color: #374151;
}

.modal-body {
    padding: 0 24px 24px;
}

.avatar-upload {
    text-align: center;
}

.hidden-input {
    display: none;
}

.upload-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #3b82f6;
    color: white;
    border: none;
    border-radius: 8px;
    padding: 12px 20px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: 16px;
}

.upload-btn:hover {
    background: #2563eb;
}

.upload-tip {
    color: #64748b;
    font-size: 0.9rem;
    margin: 0;
}

/* 响应式设计 */
@media (max-width: 968px) {
    .profile-content {
        grid-template-columns: 1fr;
        gap: 20px;
    }

    .profile-nav {
        position: static;
        padding: 20px 0;
    }

    .nav-items {
        flex-direction: row;
        overflow-x: auto;
        gap: 8px;
        padding: 0 20px;
    }

    .nav-item {
        flex-shrink: 0;
        padding: 12px 20px;
        border-radius: 12px;
        border-left: none;
        white-space: nowrap;
    }

    .nav-item.active {
        background: rgba(0, 200, 81, 0.1);
        border-left: none;
    }
}

@media (max-width: 768px) {
    .profile-container {
        padding: 20px 16px;
    }

    .page-title {
        font-size: 2.5rem;
    }

    .user-card {
        padding: 24px;
    }

    .user-avatar-section {
        flex-direction: column;
        text-align: center;
        gap: 20px;
    }

    .user-avatar {
        width: 100px;
        height: 100px;
    }

    .content-section {
        padding: 24px;
    }

    .form-row {
        grid-template-columns: 1fr;
        gap: 0;
    }
}

@media (max-width: 480px) {
    .profile-container {
        padding: 16px 12px;
    }

    .page-title {
        font-size: 2rem;
    }

    .user-card {
        padding: 20px;
    }

    .content-section {
        padding: 20px;
    }

    .nav-items {
        padding: 0 12px;
    }

    .nav-item {
        padding: 10px 16px;
        font-size: 0.9rem;
    }

    .modal-content {
        margin: 20px;
        width: calc(100% - 40px);
    }
}
</style>